import {
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  TimePicker,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";

const withRequiredNoWhitespace = (rules) => {
  if (!rules?.required) return rules;

  const requiredMessage =
    typeof rules.required === "string" ? rules.required : rules.required?.message;

  const noWhitespace = (value) => {
    if (value == null) return true;
    if (typeof value === "string") {
      return value.trim().length > 0 || requiredMessage || "This field is required";
    }
    if (typeof value === "object") {
      const label = value?.label;
      const val = value?.value;
      if (typeof label === "string" && label.trim().length === 0) {
        return requiredMessage || "This field is required";
      }
      if (typeof val === "string" && val.trim().length === 0) {
        return requiredMessage || "This field is required";
      }
    }
    return true;
  };

  const validate = rules.validate;
  if (!validate) return { ...rules, validate: noWhitespace };
  if (typeof validate === "function") {
    return { ...rules, validate: { base: validate, noWhitespace } };
  }
  if (typeof validate === "object") {
    if (validate.noWhitespace) return rules;
    return { ...rules, validate: { ...validate, noWhitespace } };
  }
  return rules;
};

export const InputField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    fieldKey,
    className,
    getValue,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);
  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        // fieldKey={fieldKey}
        className={className}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={withRequiredNoWhitespace(rules)}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              value={value}
              onChange={(e) => {
                onChange(e);
                props?.onChange && props.onChange(e.target.value);
              }}
              onBlur={props.onBlur}
              {...iProps}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const NumberInputField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    fieldKey,
    className,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        fieldKey={fieldKey}
        className={className}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={withRequiredNoWhitespace(rules)}
          render={({ field: { onChange, onBlur, value } }) => (
            <InputNumber
              value={value}
              onChange={(e) => {
                onChange(e);
              }}
              onBlur={props.onBlur}
              {...iProps}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const InputHiddenField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    fieldKey,
    className,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
        fieldKey={fieldKey}
        className={className}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={withRequiredNoWhitespace(rules)}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              type="hidden"
              value={value}
              onChange={onChange}
              onBlur={props.onBlur}
              {...iProps}
            />
          )}
        />
      </Form.Item>
    </>
  );
};
export const InputPassword = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue || initValue == 0 ? initValue : ""}
          rules={withRequiredNoWhitespace(rules)}
          // render={()=> (<Input.Password {...iProps} /> )}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <Input.Password
              value={props.disabled ? "" : value}
              ref={ref}
              onChange={(e) => {
                onChange(e);
                props.onChange && props.onChange(e);
              }}
              onBlur={props.onBlur}
              {...iProps}
              disabled={props.disabled}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const SelectField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    selectOption,
    isRequired,
    initValue,
    validate,
    validMessage,
    field,
    navElement,
    isDisabled,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      label={label}
      validateStatus={validate}
      help={validMessage}
      {...field}
    >
      <Controller
        name={fieldname}
        control={control}
        rules={withRequiredNoWhitespace(rules)}
        defaultValue={initValue ? initValue : ""}
        render={({ field: { onChange, onBlur, value } }) => (
          <ReactSelect
            value={value}
            isDisabled={isDisabled}
            styles={{
              control: (val) => ({
                ...val,
                minHeight: navElement ? 45 : 32,
                ...(navElement && { borderWidth: 0, borderRightWidth: "1px" }),
              }),
              valueContainer: (vcontain) => ({
                ...vcontain,
                padding: "0 8px",
                textTransform: "capitalize",
              }),
              dropdownIndicator: (icontain) => ({
                ...icontain,
                padding: 5,
                ...(navElement && { color: "#000000" }),
              }),
              indicatorSeparator: (icontain) => ({
                ...icontain,
                backgroundColor: "#fff",
              }),
              option: (vcontain) => ({
                ...vcontain,
                textTransform: "capitalize",
              }),
              menu: (baseStyles) => ({ ...baseStyles, zIndex: 10 }),
              placeholder: (place) => ({
                ...place,
                color: "rgba(0,0,0,.3)",
                ...(navElement && { fontSize: "18px" }),
              }),
            }}
            onChange={(e) => {
              onChange(e);
              props.onChange && props.onChange(e);
            }}
            onBlur={props.onBlur}
            {...iProps}
            options={selectOption}
            theme={(theme) => ({
              ...theme,
              borderRadius: 2,
              colors: { ...theme.colors, primary: "#767676" },
            })}
          />
        )}
      />
    </Form.Item>
  );
};

export const SelectCreateAbleField = (props) => {
  const {
    fieldname,
    isClearable,
    isLoading,
    isDisabled,
    label,
    control,
    iProps,
    isRequired,
    validate,
    validMessage,
    tooltip,
    selectOption,
    onCreateOption,
    defaultValue,
    rules,
  } = props;

  const customStyles = {
    control: (base) => ({ ...base, minHeight: 32 }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
      textTransform: "capitalize",
    }),
    dropdownIndicator: (base) => ({ ...base, padding: 5 }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: "#fff" }),
    menu: (base) => ({ ...base, zIndex: 10 }),
    placeholder: (place) => ({ ...place, color: "rgba(0,0,0,.3)" }),
  };

  return (
    <>
      <Form.Item required={isRequired} label={label} tooltip={tooltip} validateStatus={validate} help={validMessage}>
        <Controller
          name={fieldname}
          control={control}
          defaultValue={defaultValue}
          rules={withRequiredNoWhitespace(rules)}
          render={({ field }) => (
            <CreatableSelect
              {...iProps}
              value={field.value}
              onChange={field.onChange}
              options={selectOption}
              isClearable={isClearable}
              isLoading={isLoading}
              isDisabled={isDisabled}
              onCreateOption={onCreateOption}
              styles={customStyles}
              theme={(theme) => ({
                ...theme,
                colors: { ...theme.colors, primary: "#767676" },
              })}
            />
          )}
        />
      </Form.Item>
    </>
  );
};

export const SelectNormal = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    selectOption,
    isRequired,
    validate,
    validMessage,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      label={label}
      validateStatus={validate}
      help={validMessage}
      noStyle={props.noStyle}
    >
      <Controller
        name={fieldname}
        control={control}
        defaultValue={initValue ? initValue : ""}
        rules={withRequiredNoWhitespace(rules)}
        render={() => <Select {...iProps}>{selectOption}</Select>}
      />
    </Form.Item>
  );
};

export const DateField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    maxDate,
    selected,
    startDate,
    endDate,
    initValue,
    isRequired,
    validate,
    validMessage,
    isEndDateGrater,
    disabledDate,
  } = props;

  useEffect(() => {
    if (props.valueGot) {
      props.setValue(fieldname, props.valueGot);
    }
  }, []);
  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue ? dayjs(initValue, "YYYY-MM-DD") : ""}
          rules={withRequiredNoWhitespace(rules)}
          render={({ field: { onChange, value, ref } }) => {
            return (
              <DatePicker
                ref={ref}
                style={{ width: "100%" }}
                disabledDate={disabledDate ? disabledDate : undefined}
                value={props.disabled ? "" : value}
                onChange={(e) => {
                  onChange(e);
                  props.onChange && props.onChange(e);
                }}
                onBlur={props.onBlur}
                {...iProps}
              />
            );
          }}
        />
      </Form.Item>
    </>
  );
};

export const InputCheckbox = (props) => {
  const {
    fieldname,
    label,
    control,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    isDisabled,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, []);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      validateStatus={validate}
      help={validMessage}
      valuePropName="checked"
      noStyle
    >
      <Controller
        name={fieldname}
        control={control}
        rules={withRequiredNoWhitespace(rules)}
        defaultValue={initValue ? initValue : ""}
        render={({ field: { value, onChange } }) => {
          return (
            <Checkbox
              checked={value}
              defaultChecked={true}
              className={props.class}
              disabled={isDisabled}
              onChange={(e) => {
                onChange(e.target.checked);
              }}
            >
              {label}
            </Checkbox>
          );
        }}
      />
    </Form.Item>
  );
};

export const TimeField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue ? initValue : ""}
          rules={withRequiredNoWhitespace(rules)}
          render={() => <TimePicker style={{ width: "100%" }} {...iProps} />}
        />
      </Form.Item>
    </>
  );
};

export const TimeRangeField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <>
      <Form.Item
        required={isRequired ? isRequired : false}
        label={label}
        validateStatus={validate}
        help={validMessage}
      >
        <Controller
          name={fieldname}
          control={control}
          defaultValue={initValue ? initValue : ""}
          rules={withRequiredNoWhitespace(rules)}
          render={() => (
            <TimePicker.RangePicker style={{ width: "100%" }} {...iProps} />
          )}
        />
      </Form.Item>
    </>
  );
};
export const TextAreaField = (props) => {
  const {
    fieldname,
    label,
    control,
    iProps,
    rules,
    initValue,
    isRequired,
    validate,
    validMessage,
    textAreaHeight,
  } = props;

  useEffect(() => {
    props.valueGot && props.setValue(fieldname, props.valueGot);
  }, [props.valueGot]);

  return (
    <Form.Item
      required={isRequired ? isRequired : false}
      label={label}
      validateStatus={validate}
      help={validMessage}
      className={props.classes}
    >
      <Controller
        name={fieldname}
        control={control}
        defaultValue={initValue ? initValue : ""}
        rules={withRequiredNoWhitespace(rules)}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Input.TextArea
            value={props.disabled ? "" : value}
            ref={ref}
            key={initValue}
            style={{ maxHeight: 500 }}
            onChange={(e) => {
              onChange(e);
              props.onChange && props.onChange(e);
            }}
            onBlur={props.onBlur}
            {...iProps}
            disabled={props.disabled}
          />
        )}
      />
    </Form.Item>
  );
};
